/**
 * Viewer semplice per immagini diagnostiche DICOM convertite in PNG.
 */

import { useState, useRef, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DicomViewerProps {
  imageUrl: string;
  alt?: string;
  className?: string;
}

export function DicomViewer({ imageUrl, alt = "Immagine DICOM", className = "" }: DicomViewerProps) {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.25;

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(prev => {
      const newZoom = prev + delta;
      return Math.max(MIN_ZOOM, Math.min(newZoom, MAX_ZOOM));
    });
  }, []);

  return (
    <div className={`relative bg-gray-950 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
        <div className="flex items-center gap-1.5">
          <Button
            variant="secondary"
            size="icon"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="size-7"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>

          <span className="text-white text-xs font-medium min-w-[50px] text-center tabular-nums">
            {Math.round(zoom * 100)}%
          </span>

          <Button
            variant="secondary"
            size="icon"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="size-7"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1.5">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReset}
            className="text-xs"
            title="Reset view"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={handleReset}
            className="size-7"
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Image Container */}
      <div
        ref={containerRef}
        className="w-full h-96 cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      >
        <div
          className="w-full h-full flex items-center justify-center overflow-hidden"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
          }}
        >
          <img
            src={imageUrl}
            alt={alt}
            className="max-w-none"
            draggable={false}
            style={{
              imageRendering: zoom > 1 ? 'pixelated' : 'auto',
            }}
          />
        </div>
      </div>

      {/* Info overlay */}
      <div className="absolute bottom-2 right-2 z-10 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5">
        <p className="text-white/70 text-[11px]">
          Drag per spostare • Scroll per zoom
        </p>
      </div>
    </div>
  );
}
